import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Save } from 'lucide-react'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/Checkbox'
import { Select } from '@/components/ui/Select'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { useUser } from '@/hooks/useUsers'
import { UserModel, UserRole } from '@/models/user.model'
import { usersService, UpdateUserPayload } from '@/services/usersService'
import { useNotifications } from '@/contexts/NotificationContext'
import { paths } from '@/routes/path'

type FormValues = {
  firstName: string
  lastName: string
  email: string
  role: UserRole | ''
  phone: string
  title: string
  bio: string
  isActive: boolean
}

export function UserEditPage() {
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { addNotification } = useNotifications()

  const {
    data: user,
    isLoading,
    isError,
    error,
  } = useUser(userId)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<FormValues>({
    mode: 'onBlur',
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      role: '' as UserRole | '',
      phone: '',
      title: '',
      bio: '',
      isActive: true,
    },
  })

  useEffect(() => {
    if (!user) return

    reset({
      firstName: user.firstName ?? '',
      lastName: user.lastName ?? '',
      email: user.email ?? '',
      role: (user.role as UserRole) ?? '' as UserRole | '',
      phone: user.phone ?? '',
      title: user.programmeTitle ?? user.program ?? '',
      bio: user.bio ?? '',
      isActive: Boolean(user.isActive),
    })
  }, [user, reset])

  const updateMutation = useMutation({
    mutationFn: async (values: FormValues) => {
      if (!userId) {
        throw new Error('Invalid user identifier')
      }

      const payload: UpdateUserPayload = {
        firstName: values.firstName,
        lastName: values.lastName,
        role: values.role || undefined,
        isActive: values.isActive,
        phone: values.phone || null,
        bio: values.bio || null,
        title: values.title || null,
      }

      return usersService.updateUser(userId, payload)
    },
    onSuccess: (updatedUser: UserModel) => {
      queryClient.invalidateQueries({ queryKey: ['users', 'list'] })
      if (updatedUser.id) {
        queryClient.invalidateQueries({ queryKey: ['users', 'detail', updatedUser.id] })
      }

      addNotification({
        title: 'User updated successfully',
        message: `${updatedUser.fullName || 'User'} has been updated.`,
        type: 'general',
        priority: 'low',
        isRead: false,
        isArchived: false,
      })

      const destinationId = updatedUser.id ?? userId
      navigate(destinationId ? paths.app.users.detail.build(destinationId) : paths.app.users.root.absolute)
    },
    onError: (mutationError: unknown) => {
      const message = mutationError instanceof Error ? mutationError.message : 'Unable to update user'
      addNotification({
        title: 'Update failed',
        message,
        type: 'general',
        priority: 'medium',
        isRead: false,
        isArchived: false,
      })
    },
  })

  const onSubmit = handleSubmit(async (values) => {
    await updateMutation.mutateAsync(values)
  })

  const roleOptions = useMemo(
    () => [
      { value: '', label: 'Select role' },
      { value: UserRole.ADMIN, label: 'Admin' },
      { value: UserRole.INSTRUCTOR, label: 'Instructor' },
      { value: UserRole.LEARNER, label: 'Learner' },
    ],
    [],
  )

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (isError || !user) {
    const message = error instanceof Error ? error.message : 'We could not load this user.'
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center text-center">
        <p className="text-lg font-semibold text-secondary-900">User not available</p>
        <p className="mt-1 max-w-md text-sm text-secondary-600">{message}</p>
        <Link to={paths.app.users.root.absolute}>
          <Button variant="outline" className="mt-4">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to users
          </Button>
        </Link>
      </div>
    )
  }

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <Button
          variant="outline"
          type="button"
          onClick={() => navigate(-1)}
          className="text-secondary-600"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>

        <div className="flex items-center gap-3">
          {user.id && (
            <Link to={paths.app.users.detail.build(user.id)}>
              <Button variant="ghost" type="button" className="text-secondary-600 hover:text-secondary-900">
                View profile
              </Button>
            </Link>
          )}
          <Button
            type="submit"
            disabled={isSubmitting || !isDirty || updateMutation.isPending}
            className="flex items-center gap-2 bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-70"
          >
            <Save className="h-4 w-4" />
            Save changes
          </Button>
        </div>
      </div>

      <Card className="border border-secondary-100 shadow-sm">
        <CardHeader>
          <CardTitle className="text-xl font-semibold text-secondary-900">
            Update user information
          </CardTitle>
          <CardDescription className="text-secondary-600">
            Keep the user profile accurate. Fields marked with * are mandatory.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-medium text-secondary-700" htmlFor="firstName">
                First name *
              </label>
              <Input
                id="firstName"
                placeholder="First name"
                {...register('firstName', { required: 'First name is required' })}
              />
              {errors.firstName && (
                <p className="text-xs text-red-500">{errors.firstName.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-secondary-700" htmlFor="lastName">
                Last name *
              </label>
              <Input
                id="lastName"
                placeholder="Last name"
                {...register('lastName', { required: 'Last name is required' })}
              />
              {errors.lastName && (
                <p className="text-xs text-red-500">{errors.lastName.message}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-medium text-secondary-700" htmlFor="email">
                Email address
              </label>
              <Input id="email" type="email" disabled {...register('email')} />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-secondary-700" htmlFor="role">
                Role *
              </label>
              <Select
                id="role"
                {...register('role', { required: 'Role is required' })}
              >
                {roleOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
              {errors.role && (
                <p className="text-xs text-red-500">{errors.role.message}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-medium text-secondary-700" htmlFor="phone">
                Phone number
              </label>
              <Input id="phone" placeholder="+234 800 000 0000" {...register('phone')} />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-secondary-700" htmlFor="title">
                Course / department
              </label>
              <Input
                id="title"
                placeholder="Programme title or department"
                {...register('title')}
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-secondary-700" htmlFor="bio">
              Bio
            </label>
            <Textarea
              id="bio"
              rows={5}
              placeholder="Write a short professional summary..."
              {...register('bio')}
            />
          </div>

          <div className="rounded-lg border border-secondary-100 bg-secondary-50 px-4 py-3">
            <Checkbox
              label="Active user"
              {...register('isActive')}
            />
            <p className="mt-1 text-xs text-secondary-500">
              Deactivate a user to temporarily revoke access without deleting their history.
            </p>
          </div>
        </CardContent>
      </Card>
    </form>
  )
}
